/**
 * GradMap design tokens — local subset used by GPAHeroCard.
 *
 * The canonical design system lives in /05_specs_and_strategy/gradmap-design-system/
 * gradmap-tokens.css (v2, 2 Sep 2026). student-frontend does not yet have a global
 * tokens module — colors are scattered as inline hex across components (see
 * Val's `src/styles/college-management.css` for the closest thing, but its
 * `--gm-*` names use different semantics from the v2 design system). This file
 * is a local, typed subset of the canonical tokens so GPAHeroCard can render
 * against consistent, named values without waiting on a global refactor.
 *
 * When a global tokens module is added, delete this file and update the
 * import in GPAHeroCard.tsx — the token names here are chosen to match the
 * v2 canonical CSS variable names (minus the `--gm-` prefix and camelCased),
 * so migration will be search-and-replace.
 */

export const gpaHeroCardTokens = {
  // Ink ramp — every text color is Deep Blue or a lighter step of it (219°).
  ink:           '#121D31',
  inkStrong:     '#324569',
  inkBody:       '#475A7E',
  inkMuted:      '#596B8B',
  inkOnDark:     '#FFFFFF',

  // Accent — split by job: `accent` is a fill / marker / focus ring, never carries text.
  // `accentInk` is one step darker and passes AA for text on white.
  accent:        '#3E73F0',
  accentInk:     '#3568DE',
  accentTint:    '#D6E4FD', // avatar fill, selected chip
  accentWash:    '#EDF3FE', // hover fill, selected row

  // Surfaces & hairlines.
  surface:       '#FFFFFF',
  canvas:        '#F4F9FC',
  nested:        '#F7FBFD',
  sunken:        '#E8F1F7',
  hairline:      '#DDE7F0',
  hairlineStrong:'#C6D6E4',

  // Status — every color paired with a label or icon; color alone is never the signal.
  successInk:    '#0B7A5C',
  successWash:   '#E6F6F1',
  warningInk:    '#B45309',
  warningWash:   '#FDF3E2',
  // Chip edges: a visible outline one step darker than each wash (A-G chip boundaries).
  successEdge:   '#A7DCCB',
  warningEdge:   '#F1D3A1',
  dangerInk:     '#B02525',
  dangerWash:    '#FDEDED',

  // Radius by depth (no exceptions). A surface on the canvas is 16, anything
  // nested in one is 10, a row inside that is flat, a control is a pill. There
  // is deliberately no value between 10 and 16: two nearly-equal curves read as
  // a mistake.
  radiusSurface: '16px',
  radiusNested:  '10px',
  radiusRow:     '0',
  radiusControl: '999px',

  // Elevation — tinted Deep Blue, not black, so shadows sit correctly on the canvas.
  elevSurface:   '0 1px 2px rgba(18,29,49,.05), 0 4px 12px -4px rgba(18,29,49,.10)',
  elevRaised:    '0 2px 4px rgba(18,29,49,.06), 0 12px 28px -10px rgba(18,29,49,.16)',
  elevOverlay:   '0 4px 8px rgba(18,29,49,.08), 0 24px 48px -16px rgba(18,29,49,.24)',
  focusRing:     '0 0 0 3px rgba(62,115,240,.35)',

  // The dialog scrim. Deep Blue at 45%, matching the elevation tint rather than
  // going to black, which reads muddy over the cool canvas.
  scrim:         'rgba(18,29,49,.45)',

  // Spacing — 7-step scale.
  space1: '4px',
  space2: '8px',
  space3: '12px',
  space4: '16px',
  space5: '24px',
  space6: '32px',
  space7: '48px',

  // Typography — GradMap ships Nunito Sans as the web UI face. student-frontend
  // currently sets Manrope in App.css body; matching the existing runtime font
  // here so this component doesn't clash with the rest of the page.
  fontUi: "Manrope, 'Nunito Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",

  // Type scale. 11.5px is the floor: the design rules stop at caption and
  // nothing smaller is approved, so a 10px or 9.5px label is a bug even when it
  // fits. Sentence case everywhere, with no all-caps labels.
  textXs:  '11.5px',
  textSm:  '12.5px',
  textMd:  '14px',
  textLg:  '16px',
  textXl:  '20px',
  text2xl: '26px',

  weightRegular:  400,
  weightMedium:   500,
  weightSemibold: 600,
  weightBold:     700,
} as const;

export type GpaHeroCardTokens = typeof gpaHeroCardTokens;
